import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Crown } from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /founders - a members' club honour board.
 *
 * Aesthetic: private whiskey / cigar lounge. A dark walnut backing board
 * screwed to the wall, each Founding Member's name engraved on a brushed
 * gold plate held in place by four tiny brass screws. Header plaque is
 * larger, centered, deep-engraved like a club charter. A weathered brass
 * plate at the base records the founders who chose to stay anonymous.
 *
 * The whole thing is pure CSS - no images, no assets. Layered gradients
 * for the wood grain and gold brushing, inset shadows for the engraving,
 * and radial gradients for the screw heads.
 */
const Founders = () => {
  const [data, setData] = useState({ items: [], count: 0, anonymous: 0, cap: 80 });
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const joined = params.get('joined') === '1';

  useEffect(() => {
    fetch(`${API}/api/founders`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const remaining = Math.max(0, (data.cap || 80) - (data.count || 0));
  const soldOut = remaining === 0;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="founders-page">
      <TopNav />

      {/* Lounge backdrop: layered wood + spotlight + smoke haze */}
      <section
        className="relative overflow-hidden py-20 sm:py-28"
        style={{
          background: `
            radial-gradient(ellipse 90% 60% at 50% 15%, rgba(255,205,120,0.10), transparent 65%),
            radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.85), transparent 60%),
            linear-gradient(180deg, #100603 0%, #05020a 100%)
          `,
        }}
      >
        {/* Cigar-smoke haze */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40 mix-blend-screen"
          style={{
            background: `
              radial-gradient(circle 320px at 15% 20%, rgba(200,170,110,0.14), transparent 70%),
              radial-gradient(circle 260px at 85% 30%, rgba(180,120,60,0.10), transparent 70%),
              radial-gradient(circle 380px at 60% 85%, rgba(120,80,40,0.10), transparent 70%)
            `,
          }}
        />

        <main className="relative max-w-4xl mx-auto px-4">
          {joined && (
            <div className="mb-8 rounded border border-yellow-600/70 bg-black/50 backdrop-blur px-4 py-3 text-sm text-yellow-100" data-testid="founders-joined-banner">
              Welcome, Founder. Your plate is being engraved. It will appear here once Ryan approves the name.
            </div>
          )}

          {/* THE HONOUR BOARD - dark walnut plank with a rope-effect frame */}
          <div
            className="relative rounded-[10px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,205,120,0.08)]"
            style={{
              padding: '46px 28px 40px',
              // Walnut wood: layered radial + repeating linear for the grain
              background: `
                radial-gradient(ellipse at 30% 20%, #4a2818 0%, #2a1408 40%, #1a0a04 80%),
                repeating-linear-gradient(88deg, rgba(0,0,0,0.28) 0px, rgba(0,0,0,0.28) 2px, transparent 2px, transparent 8px),
                repeating-linear-gradient(92deg, rgba(255,180,100,0.05) 0px, rgba(255,180,100,0.05) 1px, transparent 1px, transparent 11px)
              `,
              backgroundBlendMode: 'multiply, overlay, normal',
            }}
            data-testid="founders-board"
          >
            {/* Inner bevel - lighter frame stroke to sell the depth */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-2 rounded-[6px]"
              style={{
                boxShadow: `
                  inset 0 0 0 1px rgba(255,205,120,0.18),
                  inset 0 2px 4px rgba(255,220,150,0.15),
                  inset 0 -3px 8px rgba(0,0,0,0.6)
                `,
              }}
            />

            {/* CORNER BOARD SCREWS - four bigger brass screws holding the board itself */}
            {[
              { top: 14, left: 14 },
              { top: 14, right: 14 },
              { bottom: 14, left: 14 },
              { bottom: 14, right: 14 },
            ].map((pos, i) => (
              <span key={i} aria-hidden className="absolute h-3.5 w-3.5 rounded-full" style={{
                ...pos,
                background: 'radial-gradient(circle at 35% 30%, #f6d67a, #b8862a 50%, #6a4a10 100%)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.9), inset 0 -1px 1px rgba(0,0,0,0.5)',
              }}>
                {/* Screw slot */}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span style={{
                    width: '70%', height: '1px', background: 'rgba(0,0,0,0.6)', transform: 'rotate(35deg)',
                  }} />
                </span>
              </span>
            ))}

            {/* HEADER PLAQUE - larger, centered, deep-engraved */}
            <div className="relative flex flex-col items-center pt-2 pb-6">
              <p className="text-yellow-600/80 text-[10px] font-black uppercase tracking-[0.5em] mb-3 flex items-center gap-2">
                <Crown className="h-3 w-3" />
                Established 2026
                <Crown className="h-3 w-3" />
              </p>

              <div className="relative w-full max-w-2xl" data-testid="founders-header-plate">
                <GoldPlate wide>
                  <span
                    className="block text-center font-black leading-tight"
                    style={{
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      fontSize: 'clamp(20px, 4vw, 32px)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#1a0a04',
                      textShadow: '0 1px 0 rgba(255,235,180,0.5), 0 -1px 1px rgba(0,0,0,0.55)',
                    }}
                  >
                    The Outlaw Media
                    <br />
                    <span style={{ fontStyle: 'italic', letterSpacing: '0.15em', fontSize: '0.72em' }}>
                      Founders Board
                    </span>
                  </span>
                </GoldPlate>
              </div>

              <p className="mt-6 text-center text-zinc-400 max-w-xl text-sm sm:text-base leading-relaxed font-serif italic">
                Eighty people and businesses paid to keep an independent
                Melbourne crime desk on the air. Their names remain here, engraved,
                for as long as we&rsquo;re publishing.
              </p>
            </div>

            {/* THE PLATES */}
            <div className="relative mt-4">
              {loading ? (
                <p className="text-center text-yellow-700/80 text-sm py-8 tracking-widest uppercase">Polishing plates&hellip;</p>
              ) : data.items.length === 0 ? (
                <div className="max-w-md mx-auto py-6" data-testid="founders-empty">
                  <GoldPlate>
                    <span
                      className="block text-center italic tracking-wider"
                      style={{
                        fontFamily: 'Georgia, serif',
                        color: '#3a2210',
                        fontSize: '15px',
                        textShadow: '0 1px 0 rgba(255,235,180,0.5)',
                      }}
                    >
                      First plate awaiting engraving
                    </span>
                  </GoldPlate>
                </div>
              ) : (
                <ol
                  data-testid="founders-list"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 py-2"
                >
                  {data.items.map((f, i) => (
                    <li key={`${f.outlaw_number || i}-${f.display_name}`} data-testid={`founder-plate-${i}`}>
                      {f.photo_url ? (
                        <PortraitPlate name={f.display_name} photo={f.photo_url} month={f.month} />
                      ) : (
                        <GoldPlate>
                          <span
                            className="block text-center font-black leading-tight"
                            style={{
                              fontFamily: 'Georgia, "Times New Roman", serif',
                              color: '#1a0a04',
                              fontSize: 'clamp(15px, 2vw, 19px)',
                              letterSpacing: '0.09em',
                              textTransform: 'uppercase',
                              textShadow: '0 1px 0 rgba(255,235,180,0.55), 0 -1px 1px rgba(0,0,0,0.55)',
                            }}
                          >
                            {f.display_name}
                          </span>
                          {f.month && (
                            <span
                              className="block text-center mt-1 italic"
                              style={{
                                fontFamily: 'Georgia, serif',
                                color: '#4a2b12',
                                fontSize: '10px',
                                letterSpacing: '0.35em',
                                textTransform: 'uppercase',
                                textShadow: '0 1px 0 rgba(255,235,180,0.4)',
                              }}
                            >
                              Since {f.month}
                            </span>
                          )}
                        </GoldPlate>
                      )}
                    </li>
                  ))}
                </ol>
              )}

              {/* Anonymous line only shown when there are also named plates so it reads as extra context. */}
              {data.anonymous > 0 && data.items.length > data.anonymous && (
                <div className="mt-8 max-w-md mx-auto" data-testid="founders-anonymous-plate">
                  <BrassPlate>
                    <span
                      className="block text-center italic"
                      style={{
                        fontFamily: 'Georgia, serif',
                        color: '#2a1808',
                        fontSize: '13px',
                        letterSpacing: '0.15em',
                        textShadow: '0 1px 0 rgba(210,170,110,0.45)',
                      }}
                    >
                      &mdash; and {data.anonymous} {data.anonymous === 1 ? 'founder' : 'founders'} who requested no name &mdash;
                    </span>
                  </BrassPlate>
                </div>
              )}
            </div>

            {/* Charter footnote engraved into a small plate */}
            <div className="relative mt-10 flex justify-center">
              <p
                className="text-center italic text-yellow-600/70 text-[11px] tracking-[0.3em] uppercase"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Lifetime seat &middot; No renewals &middot; No refunds
              </p>
            </div>
          </div>

          {/* CTA below the board */}
          <div className="mt-10 text-center" data-testid="founders-cta-row">
            {soldOut ? (
              <p className="text-sm text-zinc-400" data-testid="founders-sold-out">
                <strong className="text-yellow-400">The Board is full.</strong>{' '}
                Outlaw Access remains open at $13 a month or $130 a year.
              </p>
            ) : (
              <>
                <p className="text-yellow-500 text-[11px] font-black uppercase tracking-[0.45em] mb-3">
                  {remaining} of {data.cap || 80} plates remaining
                </p>
                <Link
                  to="/subscribe"
                  data-testid="founders-claim-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded font-black text-[11px] uppercase tracking-[0.35em] transition-all"
                  style={{
                    background: 'linear-gradient(180deg, #f6d67a 0%, #d19f2f 50%, #8a5f10 100%)',
                    color: '#1a0a04',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,240,190,0.7), inset 0 -2px 3px rgba(0,0,0,0.35)',
                  }}
                >
                  <Crown className="h-3.5 w-3.5" />
                  Reserve a plate
                </Link>
              </>
            )}
          </div>
        </main>
      </section>

      <SiteFooter />
    </div>
  );
};

/**
 * Portrait-under-glass plate: gold-embossed frame with a small photo behind
 * a subtle glass sheen and an engraved name-plate strip beneath. Used on the
 * public wall when a founder has uploaded a photo.
 */
const PortraitPlate = ({ name, photo, month }) => (
  <div
    className="relative rounded-[4px]"
    style={{
      padding: '5px',
      background: 'linear-gradient(135deg, #f7dc8b 0%, #cd982e 32%, #8a6015 62%, #f5dc95 100%)',
      boxShadow: `
        0 4px 10px rgba(0,0,0,0.7),
        inset 0 1px 1px rgba(255,240,190,0.8),
        inset 0 -2px 4px rgba(80,45,10,0.5),
        inset 0 0 0 1px rgba(180,120,40,0.6)
      `,
    }}
  >
    <div
      className="relative overflow-hidden rounded-[2px]"
      style={{
        aspectRatio: '4 / 3',
        background: '#100600',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.9), inset 0 3px 6px rgba(0,0,0,0.7)',
      }}
    >
      <img
        src={photo}
        alt={name || 'Founder portrait'}
        loading="lazy"
        className="w-full h-full object-cover"
      />
      {/* Glass diagonal gloss */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(130deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.02) 35%, transparent 55%)',
        }}
      />
    </div>
    {/* Engraved name plate strip */}
    <div
      className="relative mt-1 rounded-[2px]"
      style={{
        padding: '6px 8px',
        background: `
          repeating-linear-gradient(90deg, rgba(255,230,170,0.35) 0px, rgba(255,230,170,0.35) 1px, transparent 1px, transparent 3px),
          linear-gradient(180deg, #f4d67a 0%, #e0b148 30%, #b98726 55%, #d3a44a 78%, #f5dc95 100%)
        `,
        boxShadow: 'inset 0 0 0 1px rgba(120,80,20,0.55), inset 0 1px 1px rgba(255,240,190,0.6)',
      }}
    >
      <span
        className="block text-center font-black leading-tight"
        style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          color: '#1a0a04',
          fontSize: 'clamp(12px, 1.6vw, 15px)',
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          textShadow: '0 1px 0 rgba(255,235,180,0.55), 0 -1px 1px rgba(0,0,0,0.55)',
        }}
      >
        {name}
      </span>
      {month && (
        <span
          className="block text-center italic"
          style={{
            fontFamily: 'Georgia, serif',
            color: '#4a2b12',
            fontSize: '9px',
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            textShadow: '0 1px 0 rgba(255,235,180,0.4)',
          }}
        >
          Since {month}
        </span>
      )}
    </div>
  </div>
);

/**
 * A single engraved gold plate. Brushed gold gradient, four brass screws at
 * the corners, inner inset shadow so the text reads as engraved. `wide` bumps
 * the vertical padding for the header plaque.
 */
const GoldPlate = ({ children, wide = false }) => (
  <div
    className={`relative rounded-[3px] ${wide ? 'py-6 px-6 sm:py-8 sm:px-10' : 'py-4 px-5 sm:py-5 sm:px-6'}`}
    style={{
      // Brushed gold: horizontal repeating linear + a soft top-to-bottom
      // amber-to-copper gradient to give the plate weight.
      background: `
        repeating-linear-gradient(90deg, rgba(255,230,170,0.35) 0px, rgba(255,230,170,0.35) 1px, transparent 1px, transparent 3px),
        linear-gradient(180deg, #f4d67a 0%, #e0b148 30%, #b98726 55%, #d3a44a 78%, #f5dc95 100%)
      `,
      boxShadow: `
        0 2px 4px rgba(0,0,0,0.7),
        0 6px 18px rgba(0,0,0,0.55),
        inset 0 0 0 1px rgba(180,120,40,0.6),
        inset 0 1px 1px rgba(255,240,190,0.8),
        inset 0 -2px 4px rgba(80,45,10,0.45)
      `,
    }}
  >
    {/* Faint gloss highlight strip along the top */}
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-2 top-0 h-1/3 rounded-t-[3px]"
      style={{
        background: 'linear-gradient(180deg, rgba(255,250,220,0.35) 0%, transparent 100%)',
      }}
    />

    {/* Four brass screws */}
    {['top-1 left-1', 'top-1 right-1', 'bottom-1 left-1', 'bottom-1 right-1'].map((pos, i) => (
      <span
        key={i}
        aria-hidden
        className={`absolute ${pos} h-2 w-2 rounded-full`}
        style={{
          background: 'radial-gradient(circle at 35% 30%, #f3d078, #a67918 55%, #4d3308 100%)',
          boxShadow: '0 1px 1px rgba(0,0,0,0.7), inset 0 -1px 1px rgba(0,0,0,0.5)',
        }}
      >
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            style={{
              width: '65%',
              height: '1px',
              background: 'rgba(0,0,0,0.7)',
              transform: i % 2 === 0 ? 'rotate(45deg)' : 'rotate(-40deg)',
            }}
          />
        </span>
      </span>
    ))}

    <div className="relative">{children}</div>
  </div>
);

/**
 * Weathered brass plate for the anonymous line. Slightly darker + patina.
 */
const BrassPlate = ({ children }) => (
  <div
    className="relative rounded-[3px] py-4 px-5"
    style={{
      background: `
        repeating-linear-gradient(90deg, rgba(255,210,140,0.15) 0px, rgba(255,210,140,0.15) 1px, transparent 1px, transparent 4px),
        linear-gradient(180deg, #b18a3a 0%, #8a6828 40%, #614716 70%, #7d5a20 100%)
      `,
      boxShadow: `
        0 2px 4px rgba(0,0,0,0.6),
        inset 0 0 0 1px rgba(70,45,10,0.7),
        inset 0 1px 1px rgba(240,205,135,0.35),
        inset 0 -2px 4px rgba(20,10,0,0.5)
      `,
    }}
  >
    {['top-1 left-1', 'top-1 right-1', 'bottom-1 left-1', 'bottom-1 right-1'].map((pos, i) => (
      <span
        key={i}
        aria-hidden
        className={`absolute ${pos} h-1.5 w-1.5 rounded-full`}
        style={{
          background: 'radial-gradient(circle at 35% 30%, #c9a558, #5e4110 60%, #2a1a05 100%)',
          boxShadow: '0 1px 1px rgba(0,0,0,0.8)',
        }}
      />
    ))}
    <div className="relative">{children}</div>
  </div>
);

export default Founders;
